import type { ReactNode } from 'react';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, type TooltipProps, XAxis, YAxis } from 'recharts';
import { AXIS_TICK, GRID_STROKE, TOOLTIP_CONTENT_STYLE } from '@/components/charts/recharts-theme';
import { uniqueSorted } from '@/utils/array';
import { formatCompactUsd, formatPercent, formatUsd } from '@/utils/format';
import { isFiniteNumber } from '@/utils/number';
import { stableStringify } from '@/utils/object';
import { type OutOfBookInput, outOfBookCut, payoutFraction, timeAtKpiForPayout, type VolatilityLadder, volatilityWideningAbove } from './program-curves';

// The draft carries the accent; the saved settings recede to grey so the change reads as "from grey to blue".
const DRAFT_COLOR = 'hsl(var(--primary))';
const SAVED_COLOR = 'hsl(var(--muted-foreground))';
const MARKER_COLOR = 'hsl(var(--foreground) / 0.35)';
const SURFACE_RING = 'hsl(var(--card))';
const CHART_HEIGHT = 176;
const CHART_MARGIN = { top: 16, right: 12, bottom: 0, left: 0 };
const Y_AXIS_WIDTH = 48;
const HEADROOM = 1.15;
const X_OVERSHOOT = 1.3;
const PERCENT_TICKS = [0, 25, 50, 75, 100];
const SERIES_LABELS = { draft: 'Draft', saved: 'Saved' } as const;
const HALF = 0.5;

type Series = keyof typeof SERIES_LABELS;
type Point = { x: number } & Partial<Record<Series, number>>;

const uniqueAscending = (values: number[]) => uniqueSorted(values.filter(Number.isFinite), (left, right) => left - right);

const differs = (draft: unknown, saved: unknown) => saved !== undefined && stableStringify(draft) !== stableStringify(saved);

const LegendKey = ({ series }: { series: Series }) => (
  <span className="inline-flex items-center gap-1.5">
    <span aria-hidden className="h-0.5 w-4 rounded-full" style={{ backgroundColor: series === 'draft' ? DRAFT_COLOR : SAVED_COLOR }} />
    {SERIES_LABELS[series]}
  </span>
);
LegendKey.displayName = 'LegendKey';

interface ChartFigureProps {
  title: string;
  isCompared: boolean;
  note?: ReactNode;
  children: ReactNode;
}

export const ChartFigure = ({ title, isCompared, note, children }: ChartFigureProps) => (
  <figure className="flex min-w-0 flex-col gap-3 rounded-lg bg-muted/40 p-4">
    <figcaption className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <span className="text-sm font-medium">{title}</span>
      {isCompared ? (
        <span className="flex gap-3 text-xs text-muted-foreground">
          <LegendKey series="saved" />
          <LegendKey series="draft" />
        </span>
      ) : null}
    </figcaption>
    {children}
    {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
  </figure>
);
ChartFigure.displayName = 'ChartFigure';

const ChartPlaceholder = ({ children }: { children: ReactNode }) => (
  <p className="flex items-center justify-center text-center text-xs text-muted-foreground" style={{ height: CHART_HEIGHT }}>
    {children}
  </p>
);
ChartPlaceholder.displayName = 'ChartPlaceholder';

interface CurveTooltipProps extends TooltipProps<number, string> {
  describeX: (x: number) => string;
  formatValue: (value: number, series: Series) => string;
}

const CurveTooltip = ({ active, payload, describeX, formatValue }: CurveTooltipProps) => {
  const point = payload?.[0]?.payload as Point | undefined;
  if (!active || !point) return null;
  const rows = (['draft', 'saved'] as const).filter((series) => isFiniteNumber(point[series]));
  return (
    <div className="flex flex-col gap-1 px-3 py-2 text-xs" style={TOOLTIP_CONTENT_STYLE}>
      <span className="font-medium">{describeX(point.x)}</span>
      {rows.map((series) => (
        <span className="inline-flex items-center gap-1.5 tabular-nums" key={series}>
          <span aria-hidden className="h-0.5 w-3 rounded-full" style={{ backgroundColor: series === 'draft' ? DRAFT_COLOR : SAVED_COLOR }} />
          {rows.length > 1 ? `${SERIES_LABELS[series]}: ` : null}
          {formatValue(point[series] as number, series)}
        </span>
      ))}
    </div>
  );
};
CurveTooltip.displayName = 'CurveTooltip';

interface CurveChartProps {
  /** Read out to screen readers in place of the plot */
  summary: string;
  points: Point[];
  isCompared: boolean;
  xDomain: [number, number];
  xTicks?: number[];
  formatX: (x: number) => string;
  yDomain: [number, number];
  yTicks: number[];
  formatY: (y: number) => string;
  describeX: (x: number) => string;
  formatValue: (value: number, series: Series) => string;
  isStep?: boolean;
  markers?: { x: number; label: string }[];
}

const CurveChart = ({ summary, points, isCompared, xDomain, xTicks, formatX, yDomain, yTicks, formatY, describeX, formatValue, isStep, markers = [] }: CurveChartProps) => (
  <div aria-label={summary} role="img">
    <ResponsiveContainer height={CHART_HEIGHT} width="100%">
      <LineChart data={points} margin={CHART_MARGIN}>
        <CartesianGrid stroke={GRID_STROKE} vertical={false} />
        <XAxis allowDataOverflow dataKey="x" domain={xDomain} tick={AXIS_TICK} tickFormatter={formatX} ticks={xTicks} type="number" />
        <YAxis allowDataOverflow domain={yDomain} tick={AXIS_TICK} tickFormatter={formatY} ticks={yTicks} type="number" width={Y_AXIS_WIDTH} />
        {markers.map((marker) => (
          <ReferenceLine
            key={marker.label}
            // A marker in the right half is labelled on its left so the text stays inside the plot.
            label={{
              value: marker.label,
              position: marker.x > (xDomain[0] + xDomain[1]) / 2 ? 'insideTopRight' : 'insideTopLeft',
              fill: 'hsl(var(--muted-foreground))',
              fontSize: 11,
            }}
            stroke={MARKER_COLOR}
            strokeDasharray="3 3"
            x={marker.x}
          />
        ))}
        <Tooltip content={<CurveTooltip describeX={describeX} formatValue={formatValue} />} cursor={{ stroke: MARKER_COLOR }} isAnimationActive={false} />
        {isCompared ? (
          <Line
            activeDot={{ r: 4, fill: SAVED_COLOR, stroke: SURFACE_RING, strokeWidth: 2 }}
            dataKey="saved"
            dot={false}
            isAnimationActive={false}
            stroke={SAVED_COLOR}
            strokeOpacity={0.8}
            strokeWidth={1.5}
            type={isStep ? 'stepAfter' : 'linear'}
          />
        ) : null}
        <Line
          activeDot={{ r: 4, fill: DRAFT_COLOR, stroke: SURFACE_RING, strokeWidth: 2 }}
          dataKey="draft"
          dot={false}
          isAnimationActive={false}
          stroke={DRAFT_COLOR}
          strokeLinecap="round"
          strokeWidth={2}
          type={isStep ? 'stepAfter' : 'linear'}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);
CurveChart.displayName = 'CurveChart';

export interface PayTerms {
  threshold: number;
  compensation: number;
  exponent_start?: number | null;
  exponent?: number | null;
}

const isOptionalNumber = (value: number | null | undefined) => value === undefined || value === null || isFiniteNumber(value);

const isDrawablePay = (terms: PayTerms) =>
  isFiniteNumber(terms.threshold) && isFiniteNumber(terms.compensation) && isOptionalNumber(terms.exponent_start) && isOptionalNumber(terms.exponent);

const curvePayout = (terms: PayTerms, timeAtKpi: number) => payoutFraction(timeAtKpi, terms.threshold, terms.exponent_start, terms.exponent);

const describePay = (terms: PayTerms) => {
  const full = `full pay from ${formatPercent(terms.threshold / 100)} of the run at KPI`;
  const hasCurve = terms.exponent !== undefined && terms.exponent !== null && terms.threshold > 0;
  const half = timeAtKpiForPayout(HALF, terms.threshold, terms.exponent_start, terms.exponent);
  return hasCurve ? `${full}, half pay at ${formatPercent(half / 100)}` : `${full}, nothing below`;
};

interface PayCurveChartProps {
  title: string;
  draft: PayTerms;
  saved?: PayTerms;
  /** Top of the pay axis, so curves drawn side by side share a scale */
  maxCompensation?: number;
}

/** Monthly pay against the share of each run a bucket is met, from the payout curve in mm-program `src/backtest/pay.py`. */
export const PayCurveChart = ({ title, draft, saved, maxCompensation }: PayCurveChartProps) => {
  const compared = saved !== undefined && differs(draft, saved) && isDrawablePay(saved) ? saved : undefined;
  if (!isDrawablePay(draft)) {
    return (
      <ChartFigure isCompared={false} title={title}>
        <ChartPlaceholder>Enter a threshold and compensation to draw the pay curve.</ChartPlaceholder>
      </ChartFigure>
    );
  }
  const terms = compared ? [draft, compared] : [draft];
  const xs = uniqueAscending([
    ...Array.from({ length: 101 }, (_, index) => index),
    ...terms.flatMap((item) => [item.threshold, item.threshold - 0.01, item.exponent_start ?? 0]),
  ]).filter((x) => x >= 0 && x <= 100);
  const points = xs.map((x) => ({
    x,
    draft: curvePayout(draft, x) * draft.compensation,
    ...(compared ? { saved: curvePayout(compared, x) * compared.compensation } : {}),
  }));
  const top = Math.max(maxCompensation ?? 0, ...terms.map((item) => item.compensation)) || 1;
  const shareOf = (series: Series) => (series === 'draft' ? draft : (compared ?? draft)).compensation;
  return (
    <ChartFigure isCompared={Boolean(compared)} title={title}>
      <CurveChart
        describeX={(x) => `${formatPercent(x / 100)} of the run at KPI`}
        formatValue={(value, series) => `${formatUsd(value, 0)} · ${formatPercent(value / shareOf(series))}`}
        formatX={(x) => `${x}%`}
        formatY={formatCompactUsd}
        isCompared={Boolean(compared)}
        markers={[{ x: draft.threshold, label: 'Threshold' }]}
        points={points}
        summary={`${title}: ${describePay(draft)}, up to ${formatUsd(draft.compensation, 0)} a month.`}
        xDomain={[0, 100]}
        xTicks={PERCENT_TICKS}
        yDomain={[0, top * HEADROOM]}
        yTicks={[0, top * HALF, top]}
      />
    </ChartFigure>
  );
};
PayCurveChart.displayName = 'PayCurveChart';

const isDrawableLadder = (ladder: VolatilityLadder | undefined): ladder is VolatilityLadder =>
  ladder !== undefined && ladder.rungs_bps.length > 0 && ladder.rungs_bps.length === ladder.add_bps.length && [...ladder.rungs_bps, ...ladder.add_bps].every(isFiniteNumber);

interface VolatilityLadderChartProps {
  draft: VolatilityLadder;
  saved?: VolatilityLadder;
  /** Bucket half-widths before widening; left out for session sets, whose widths differ per session */
  baseWidths?: { b1: number; b2: number };
}

/** Width added to both buckets against the 60s EWMA, per mm-program `src/backtest/ladder.py`. */
export const VolatilityLadderChart = ({ draft, saved, baseWidths }: VolatilityLadderChartProps) => {
  const title = 'Widening by volatility';
  if (!isDrawableLadder(draft)) {
    return (
      <ChartFigure isCompared={false} title={title}>
        <ChartPlaceholder>Enter one added width per rung to draw the ladder.</ChartPlaceholder>
      </ChartFigure>
    );
  }
  const compared = differs(draft, saved) && isDrawableLadder(saved) ? saved : undefined;
  const ladders = compared ? [draft, compared] : [draft];
  const xMax = Math.max(...ladders.flatMap((ladder) => ladder.rungs_bps)) * X_OVERSHOOT;
  const points = uniqueAscending([0, ...ladders.flatMap((ladder) => ladder.rungs_bps), xMax]).map((x) => ({
    x,
    draft: volatilityWideningAbove(x, draft),
    ...(compared ? { saved: volatilityWideningAbove(x, compared) } : {}),
  }));
  const top = Math.max(...ladders.flatMap((ladder) => ladder.add_bps), 1);
  const widths = (added: number) => (baseWidths ? ` (B1 ±${baseWidths.b1 + added}, B2 ±${baseWidths.b2 + added})` : '');
  const steps = draft.rungs_bps.map((rung, index) => `above ${rung} bps +${draft.add_bps[index]}`).join(', ');
  return (
    <ChartFigure isCompared={Boolean(compared)} title={title}>
      <CurveChart
        describeX={(x) => (x === 0 ? 'Below the first rung' : `EWMA above ${x} bps`)}
        formatValue={(value) => `+${value} bps${widths(value)}`}
        formatX={(x) => `${Math.round(x)}`}
        formatY={(y) => `+${Math.round(y)}`}
        isCompared={Boolean(compared)}
        isStep
        points={points}
        summary={`${title}, added bps by 60-second EWMA in bps: ${steps}.`}
        xDomain={[0, xMax]}
        xTicks={[0, ...draft.rungs_bps]}
        yDomain={[0, top * HEADROOM]}
        yTicks={uniqueAscending([0, ...draft.add_bps])}
      />
    </ChartFigure>
  );
};
VolatilityLadderChart.displayName = 'VolatilityLadderChart';

const isDrawablePenalty = (penalty: OutOfBookInput | undefined): penalty is OutOfBookInput =>
  penalty !== undefined && [penalty.min_gap_s, penalty.full_s, penalty.max_cut_fraction].every(isFiniteNumber) && penalty.full_s > 0;

/** Cut for one gap in a run, per `qualifying_seconds` and `out_of_book_cut` in mm-program `src/backtest/pay.py`. */
export const OutOfBookChart = ({ draft, saved }: { draft: OutOfBookInput; saved?: OutOfBookInput }) => {
  const title = 'Pay cut for one gap';
  if (!isDrawablePenalty(draft)) {
    return (
      <ChartFigure isCompared={false} title={title}>
        <ChartPlaceholder>Enter the gap, full-cut time and maximum cut to draw the penalty.</ChartPlaceholder>
      </ChartFigure>
    );
  }
  const compared = differs(draft, saved) && isDrawablePenalty(saved) ? saved : undefined;
  const penalties = compared ? [draft, compared] : [draft];
  const xMax = Math.max(...penalties.flatMap((penalty) => [penalty.full_s, penalty.min_gap_s])) * X_OVERSHOOT;
  // Just past the minimum gap, where a counting gap jumps to its full length.
  const jump = xMax / 2000;
  const points = uniqueAscending([0, xMax, ...penalties.flatMap((penalty) => [penalty.min_gap_s, penalty.min_gap_s + jump, penalty.full_s])])
    .filter((x) => x <= xMax)
    .map((x) => ({ x, draft: outOfBookCut(x, draft), ...(compared ? { saved: outOfBookCut(x, compared) } : {}) }));
  const top = Math.max(...penalties.map((penalty) => penalty.max_cut_fraction)) || 0.1;
  return (
    <ChartFigure isCompared={Boolean(compared)} title={title}>
      <CurveChart
        describeX={(x) => `One ${Math.round(x)} s gap`}
        formatValue={(value) => `${formatPercent(value)} cut`}
        formatX={(x) => `${Math.round(x)}s`}
        formatY={(y) => formatPercent(y)}
        isCompared={Boolean(compared)}
        markers={draft.min_gap_s > 0 ? [{ x: draft.min_gap_s, label: 'Min gap' }] : []}
        points={points}
        summary={`${title}: nothing for gaps up to ${draft.min_gap_s} s, then rising to a ${formatPercent(draft.max_cut_fraction)} cut at ${draft.full_s} s.`}
        xDomain={[0, xMax]}
        xTicks={[0, draft.full_s * HALF, draft.full_s]}
        yDomain={[0, top * HEADROOM]}
        yTicks={[0, top * HALF, top]}
      />
    </ChartFigure>
  );
};
OutOfBookChart.displayName = 'OutOfBookChart';
